import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Shell from "../components/Shell";
import { api, eventTypeLabel, daysUntil } from "../lib/api";
import { Button } from "../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  Plus, ArrowUpRight, CalendarDays, MapPin, Repeat, AlertTriangle,
  CheckCircle2, Sparkles, Users, Wallet, ListTodo, FileUp, MessageSquare, Activity,
  ChevronDown, ChevronUp, GripVertical, X, Settings2, RotateCcw, EyeOff, Eye,
} from "lucide-react";
import ApplyTemplateDialog from "../components/ApplyTemplateDialog";
import EventTypePicker from "../components/EventTypePicker";
import RecurrenceControl, { summarizeRecurrence } from "../components/RecurrenceControl";
import CalendarWidget from "../components/CalendarWidget";
import SystemPulseBar from "../components/SystemPulseBar";
import InvitePreviewCard, { inviteRoleLabelFor } from "../components/InvitePreviewCard";
import { useRoleFilter, filterEventsByRole } from "../lib/roleFilter";
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, useSortable, verticalListSortingStrategy, arrayMove,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

export default function Dashboard() {
  const navigate = useNavigate();
  const [dash, setDash] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [templateFor, setTemplateFor] = useState(null);
  const [form, setForm] = useState({
    title: "", event_type: "wedding", event_date: "", venue: "",
    couple_names: "", description: "", cover_image: "", client_id: "",
    is_recurring: false, recurrence: null,
  });
  const [savedLayout, setSavedLayout] = useState(null);
  const [editing, setEditing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dashRes, clRes, prefsRes, msgRes] = await Promise.all([
        api.get("/dashboard"),
        api.get("/clients").catch(() => ({ data: [] })),
        api.get("/user/preferences").catch(() => ({ data: { dashboard_layout: null } })),
        api.get("/chat/dashboard-feed").catch(() => ({ data: { items: [] } })),
      ]);
      setDash({ ...dashRes.data, messages: msgRes.data?.items || [] });
      setClients(clRes.data);
      setSavedLayout(prefsRes.data?.dashboard_layout || null);
      setLastRefresh(Date.now());
    } catch (e) {
      console.error("Dashboard load failed:", e);
      toast.error("Could not load dashboard");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const persistLayout = useCallback(async (nextLayout) => {
    setSavedLayout(nextLayout);
    try {
      await api.put("/user/preferences/dashboard", nextLayout);
    } catch (e) {
      console.error("Layout save failed:", e);
      toast.error("Could not save layout");
    }
  }, []);

  const resetLayout = useCallback(async () => {
    try {
      await api.delete("/user/preferences/dashboard");
      setSavedLayout(null);
      toast.success("Layout reset to smart default");
    } catch (e) {
      console.error("Layout reset failed:", e);
      toast.error("Could not reset layout");
    }
  }, []);

  const create = async () => {
    if (!form.title || !form.event_date) {
      toast.error("Please add a title and date");
      return;
    }
    try {
      const { data } = await api.post("/events", { ...form, client_id: form.client_id || null });
      toast.success("Event created");
      setOpen(false);
      setTemplateFor(data);
    } catch (e) {
      toast.error("Could not create event");
    }
  };

  const events = dash?.events || [];
  const portfolio = dash?.portfolio || {};
  const pendingInvitations = dash?.pending_invitations || [];
  const scale = events.length === 0 ? "empty"
    : events.length === 1 ? "single"
    : events.length <= 10 ? "small"
    : "high-volume";

  // Role filter (workspace switcher) — now hoisted to global context so the
  // Shell top-nav renders the chip bar. We just push availableRoles up here
  // and consume the current filter value.
  const { value: roleFilter, setAvailableRoles } = useRoleFilter();
  const availableRoles = useMemo(() => {
    const set = new Set(events.map((e) => e.role || "owner"));
    return Array.from(set);
  }, [events]);
  useEffect(() => { setAvailableRoles(availableRoles); }, [availableRoles, setAvailableRoles]);
  const filteredEvents = useMemo(
    () => filterEventsByRole(events, roleFilter),
    [events, roleFilter],
  );
  const filteredDash = useMemo(() => (
    dash ? { ...dash, events: filteredEvents } : null
  ), [dash, filteredEvents]);

  return (
    <Shell>
      <div className="p-6 md:p-8">
        <SystemPulseBar portfolio={portfolio} lastRefresh={lastRefresh} />
        <InvitationsBanner invitations={pendingInvitations} onChanged={load} />
        {/* Header row */}
        <div className="flex items-start justify-between mb-6 gap-6 flex-wrap">
          <div>
            <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted mb-2">
              CMD.CENTER / {scale.toUpperCase().replace("-", "_")}{editing && " · EDIT_MODE"}
            </div>
            <h1 className="font-display text-3xl md:text-4xl font-black text-app tracking-tight leading-none">
              Command center
            </h1>
            <p className="mt-2 text-sm text-muted">
              {scale === "empty" && "Ready when you are."}
              {scale === "single" && "Everything about your event, at a glance."}
              {scale === "small" && `${portfolio.upcoming || 0} upcoming · ${portfolio.overdue_tasks || 0} overdue · full list in `}
              {scale === "high-volume" && `${portfolio.total_events} events tracked · ${portfolio.overdue_tasks} overdue · ${portfolio.vendors_unconfirmed} vendors awaiting confirmation · `}
              {scale !== "empty" && scale !== "single" && (
                <Link to="/events" className="underline underline-offset-2 text-app" data-testid="cmd-center-events-link">Events</Link>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {scale !== "empty" && (
              <>
                {editing ? (
                  <>
                    <button
                      onClick={resetLayout}
                      data-testid="layout-reset-btn"
                      className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] uppercase px-3 h-10 border border-hair hover:border-[#0A0A0A] text-app"
                    >
                      <RotateCcw size={12} strokeWidth={2} /> Reset
                    </button>
                    <button
                      onClick={() => setEditing(false)}
                      data-testid="layout-done-btn"
                      className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] uppercase px-3 h-10 border border-[#0A0A0A] bevel-btn-primary text-white hover:bg-black"
                    >
                      Done
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setEditing(true)}
                    data-testid="layout-edit-btn"
                    className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] uppercase px-3 h-10 border border-hair hover:border-[#0A0A0A] text-app"
                  >
                    <Settings2 size={12} strokeWidth={2} /> Customize
                  </button>
                )}
              </>
            )}
            <NewEventButton open={open} setOpen={setOpen} form={form} setForm={setForm} clients={clients} create={create} />
          </div>
        </div>

        <RoleFilterHint availableRoles={availableRoles} value={roleFilter} totalCount={events.length} filteredCount={filteredEvents.length} />

        {loading ? (
          <div className="text-muted font-mono text-xs">LOADING…</div>
        ) : scale === "empty" ? (
          <EmptyState onCreate={() => setOpen(true)} />
        ) : (
          <AdaptiveGrid
            scale={scale}
            dash={filteredDash}
            clients={clients}
            editing={editing}
            savedLayout={savedLayout}
            onLayoutChange={persistLayout}
          />
        )}
      </div>

      <ApplyTemplateDialog
        eventId={templateFor?.id}
        eventType={templateFor?.event_type}
        open={!!templateFor}
        onOpenChange={(v) => {
          if (!v && templateFor) {
            navigate(`/events/${templateFor.id}`);
            setTemplateFor(null);
          }
        }}
        onApplied={() => templateFor && navigate(`/events/${templateFor.id}`)}
      />
    </Shell>
  );
}

/* ============================================================================
 * ADAPTIVE LAYOUT + WIDGET CUSTOMIZATION
 * ==========================================================================*/

const WIDGET_META = {
  portfolio: { label: "Portfolio stats", zone: "top" },
  hero:      { label: "Hero event",      zone: "top" },
  calendar:  { label: "Calendar",        zone: "top" },
  focus:     { label: "Today's focus",   zone: "col" },
  events:    { label: "Upcoming events", zone: "col" },
  schedule:  { label: "Today's schedule", zone: "col" },
  activity:  { label: "Recent activity", zone: "col" },
  messages:  { label: "Messages",        zone: "col" },
};

function getDefaultLayout(scale) {
  if (scale === "single") {
    return {
      top: ["hero", "calendar"],
      main: ["focus", "schedule"],
      side: ["messages", "activity"],
      hidden: ["events"],
      collapsed: [],
    };
  }
  if (scale === "high-volume") {
    return {
      top: ["portfolio", "calendar"],
      main: ["focus", "schedule"],
      side: ["messages", "activity"],
      hidden: ["events"],
      collapsed: [],
    };
  }
  // small
  return {
    top: ["portfolio", "calendar"],
    main: ["focus", "messages"],
    side: ["schedule", "activity"],
    hidden: ["events"],
    collapsed: [],
  };
}

/** Merge user's saved layout with the smart default. User's wins where present. */
function mergeLayouts(scale, saved) {
  const def = getDefaultLayout(scale);
  if (!saved) return def;

  // Rebuild `top`, `main`, `side` from saved.order + saved.columns.
  const order = Array.isArray(saved.order) && saved.order.length > 0
    ? saved.order
    : [...def.top, ...def.main, ...def.side];
  const columns = saved.columns || {};
  const hidden = Array.isArray(saved.hidden) ? saved.hidden : [];
  const collapsed = Array.isArray(saved.collapsed) ? saved.collapsed : [];

  const layout = { top: [], main: [], side: [], hidden, collapsed };
  const seen = new Set();
  for (const id of order) {
    if (!WIDGET_META[id] || seen.has(id)) continue;
    seen.add(id);
    if (WIDGET_META[id].zone === "top") {
      layout.top.push(id);
    } else {
      // widget is a column widget — respect user's column choice or fall back to default
      const col = columns[id] === "side" ? "side"
        : columns[id] === "main" ? "main"
        : def.main.includes(id) ? "main"
        : def.side.includes(id) ? "side"
        : "main";
      layout[col].push(id);
    }
  }
  // Ensure every default widget appears somewhere (unless explicitly hidden)
  for (const id of [...def.top, ...def.main, ...def.side]) {
    if (seen.has(id) || hidden.includes(id)) continue;
    if (WIDGET_META[id].zone === "top") layout.top.push(id);
    else if (def.main.includes(id)) layout.main.push(id);
    else layout.side.push(id);
  }
  return layout;
}

/** Serialize a runtime layout into the flat schema the backend expects. */
function serializeLayout(layout) {
  const order = [...layout.top, ...layout.main, ...layout.side];
  const columns = {};
  for (const id of layout.main) if (WIDGET_META[id]?.zone === "col") columns[id] = "main";
  for (const id of layout.side) if (WIDGET_META[id]?.zone === "col") columns[id] = "side";
  return {
    order,
    hidden: layout.hidden || [],
    collapsed: layout.collapsed || [],
    columns,
  };
}

function AdaptiveGrid({ scale, dash, clients, editing, savedLayout, onLayoutChange }) {
  const events = dash.events || [];
  const focus = dash.todays_focus || [];
  const schedule = dash.upcoming_schedule || [];
  const activity = dash.recent_activity || [];
  const portfolio = dash.portfolio || {};

  const [layout, setLayout] = useState(() => mergeLayouts(scale, savedLayout));

  // Re-derive layout when scale changes OR when a saved layout arrives from server.
  useEffect(() => {
    setLayout(mergeLayouts(scale, savedLayout));
  }, [scale, savedLayout]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const commit = useCallback((next) => {
    setLayout(next);
    onLayoutChange?.(serializeLayout(next));
  }, [onLayoutChange]);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    // Find source + target columns
    const columnsOf = { main: layout.main, side: layout.side };
    const findCol = (id) => (columnsOf.main.includes(id) ? "main" : columnsOf.side.includes(id) ? "side" : null);
    const fromCol = findCol(active.id);
    const toCol = findCol(over.id) || (over.id === "col-main-empty" ? "main" : over.id === "col-side-empty" ? "side" : null);
    if (!fromCol || !toCol) return;

    if (fromCol === toCol) {
      const oldIndex = columnsOf[fromCol].indexOf(active.id);
      const newIndex = columnsOf[fromCol].indexOf(over.id);
      commit({ ...layout, [fromCol]: arrayMove(columnsOf[fromCol], oldIndex, newIndex) });
    } else {
      // Move across columns — insert before the over.id
      const nextFrom = columnsOf[fromCol].filter((id) => id !== active.id);
      const overIdx = columnsOf[toCol].indexOf(over.id);
      const nextTo = [...columnsOf[toCol]];
      nextTo.splice(overIdx >= 0 ? overIdx : nextTo.length, 0, active.id);
      commit({ ...layout, [fromCol]: nextFrom, [toCol]: nextTo });
    }
  };

  const toggleCollapse = (id) => {
    const collapsed = layout.collapsed.includes(id)
      ? layout.collapsed.filter((x) => x !== id)
      : [...layout.collapsed, id];
    commit({ ...layout, collapsed });
  };

  const hideWidget = (id) => {
    // Remove from wherever it lives, add to hidden
    const next = {
      ...layout,
      top: layout.top.filter((x) => x !== id),
      main: layout.main.filter((x) => x !== id),
      side: layout.side.filter((x) => x !== id),
      hidden: [...new Set([...layout.hidden, id])],
    };
    commit(next);
  };

  const showWidget = (id) => {
    const zone = WIDGET_META[id]?.zone === "top" ? "top" : "main";
    const next = {
      ...layout,
      [zone]: [...layout[zone], id],
      hidden: layout.hidden.filter((x) => x !== id),
    };
    commit(next);
  };

  const renderWidget = (id) => {
    if (layout.hidden.includes(id)) return null;
    const collapsed = layout.collapsed.includes(id);
    const commonProps = {
      widgetId: id,
      editing,
      collapsed,
      onToggleCollapse: () => toggleCollapse(id),
      onHide: () => hideWidget(id),
    };
    switch (id) {
      case "portfolio":
        return <PortfolioWidget key={id} portfolio={portfolio} scale={scale} {...commonProps} />;
      case "hero":
        return <HeroWidget key={id} ev={events[0]} clients={clients} {...commonProps} />;
      case "focus":
        return <FocusWidget key={id} items={focus} {...commonProps} />;
      case "schedule":
        return <ScheduleWidget key={id} items={schedule} dense={scale === "high-volume"} {...commonProps} />;
      case "activity":
        return <ActivityWidget key={id} items={activity} {...commonProps} />;
      case "events":
        return <EventsWidget key={id} events={events} clients={clients} dense={scale === "high-volume"} {...commonProps} />;
      case "messages":
        return <MessagesWidget key={id} items={dash.messages || []} {...commonProps} />;
      case "calendar":
        return <CalendarWidget key={id} {...commonProps} />;
      default:
        return null;
    }
  };

  const hiddenList = Object.keys(WIDGET_META).filter(
    (id) => layout.hidden.includes(id) && (id !== "hero" || scale === "single") && (id !== "portfolio" || scale !== "single"),
  );

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <div className="space-y-6" data-testid={`dashboard-scale-${scale}`}>
        {/* Top zone — full-width, non-sortable */}
        {layout.top.map((id) => renderWidget(id))}

        {/* Two-column grid — sortable */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <SortableColumn
            id="col-main-empty"
            items={layout.main}
            className="lg:col-span-2 space-y-4"
            editing={editing}
            testId="dashboard-column-main"
            renderWidget={renderWidget}
          />
          <SortableColumn
            id="col-side-empty"
            items={layout.side}
            className="space-y-4"
            editing={editing}
            testId="dashboard-column-side"
            renderWidget={renderWidget}
          />
        </div>

        {/* Hidden widgets — show-again strip in edit mode */}
        {editing && hiddenList.length > 0 && (
          <div className="border border-dashed border-hair bg-inset p-4" data-testid="hidden-widgets-tray">
            <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted mb-2">HIDDEN WIDGETS</div>
            <div className="flex items-center gap-2 flex-wrap">
              {hiddenList.map((id) => (
                <button
                  key={id}
                  onClick={() => showWidget(id)}
                  data-testid={`show-widget-${id}`}
                  className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] uppercase px-3 py-2 bevel border-hair hover:border-[#0A0A0A]"
                >
                  <Eye size={12} strokeWidth={2} /> {WIDGET_META[id].label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </DndContext>
  );
}

function SortableColumn({ id, items, className, editing, testId, renderWidget }) {
  return (
    <SortableContext items={items} strategy={verticalListSortingStrategy}>
      <div className={className} data-testid={testId}>
        {items.map((widgetId) => (
          <SortableWidgetWrapper key={widgetId} id={widgetId} editing={editing}>
            {renderWidget(widgetId)}
          </SortableWidgetWrapper>
        ))}
        {editing && items.length === 0 && (
          <div className="border border-dashed border-hair p-6 text-center font-mono text-[10px] tracking-[0.2em] uppercase text-faint">
            Drop widgets here
          </div>
        )}
      </div>
    </SortableContext>
  );
}

function SortableWidgetWrapper({ id, editing, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  if (!children) return null;
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : "auto",
  };
  // Inject drag-handle props into the child via cloneElement
  return (
    <div ref={setNodeRef} style={style}>
      {React.cloneElement(children, { dragHandleProps: editing ? { ...attributes, ...listeners } : null })}
    </div>
  );
}

/* ============================================================================
 * WIDGETS
 * ==========================================================================*/
function Widget({
  sysid, title, count, action, children, testId,
  editing, collapsed, dragHandleProps, onToggleCollapse, onHide,
}) {
  return (
    <section
      className={`bg-surface border ${editing ? "border-dashed border-[#F59E0B]" : "border-hair"}`}
      data-testid={testId}
    >
      <header className={`flex items-baseline justify-between px-4 py-3 border-b ${editing ? "border-[#F3D5B5]" : "border-hair"}`}>
        <div className="flex items-baseline gap-2 min-w-0">
          {editing && dragHandleProps && (
            <button
              type="button"
              {...dragHandleProps}
              data-testid={`drag-handle-${testId}`}
              title="Drag to reorder or move columns"
              className="cursor-grab active:cursor-grabbing text-[#F59E0B] hover:text-[#FCD34D] mr-1 self-center"
              aria-label={`Drag ${title} widget`}
            >
              <GripVertical size={14} strokeWidth={2} />
            </button>
          )}
          <h3 className="font-display text-sm font-bold text-app tracking-tight uppercase truncate">{title}</h3>
          {typeof count === "number" && (
            <span className="font-mono text-[10px] tabular-nums text-muted flex-shrink-0">[{String(count).padStart(2, "0")}]</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {action}
          {sysid && !editing && <span className="font-mono text-[10px] tracking-widest text-faint uppercase">{sysid}</span>}
          {onToggleCollapse && (
            <button
              type="button"
              onClick={onToggleCollapse}
              data-testid={`toggle-collapse-${testId}`}
              title={collapsed ? "Expand" : "Collapse"}
              className="text-muted hover:text-app"
              aria-label={collapsed ? "Expand widget" : "Collapse widget"}
            >
              {collapsed ? <ChevronDown size={13} strokeWidth={2} /> : <ChevronUp size={13} strokeWidth={2} />}
            </button>
          )}
          {editing && onHide && (
            <button
              type="button"
              onClick={onHide}
              data-testid={`hide-widget-${testId}`}
              title="Hide widget"
              className="text-muted hover:text-[#EF4444]"
              aria-label="Hide widget"
            >
              <EyeOff size={13} strokeWidth={2} />
            </button>
          )}
        </div>
      </header>
      {!collapsed && <div className="p-4">{children}</div>}
    </section>
  );
}

function HeroWidget({ ev, clients, widgetId, editing, collapsed, dragHandleProps, onToggleCollapse, onHide }) {
  if (!ev) return null;
  if (editing || collapsed) {
    // In edit mode / collapsed, render inside the standard Widget wrapper so
    // the drag handle + hide button live in a predictable place.
    return (
      <Widget
        title="Hero event" sysid="EVT.HERO" testId="widget-hero"
        editing={editing} collapsed={collapsed} dragHandleProps={dragHandleProps}
        onToggleCollapse={onToggleCollapse} onHide={onHide}
      >
        <HeroEventCardInner ev={ev} clients={clients} />
      </Widget>
    );
  }
  // Non-edit mode: raw hero card without the widget chrome (looks better).
  return <HeroEventCardInner ev={ev} clients={clients} />;
}

function HeroEventCardInner({ ev, clients }) {
  const days = daysUntil(ev.event_date);
  const isToday = days === 0;
  const soon = days >= 0 && days <= 7;
  const stripe = isToday ? "bg-[#EF4444]" : soon ? "bg-[#F59E0B]" : "bg-app";
  const client = ev.client_id ? clients.find((c) => c.id === ev.client_id) : null;
  const r = ev.readiness || {};
  const initials = (ev.title || "").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  return (
    <Link
      to={`/events/${ev.id}`}
      data-testid="hero-event-card"
      className="group block bevel border-hair relative card-lift"
    >
      <div className={`absolute left-0 top-0 bottom-0 w-1 ${stripe}`} />
      <div className="p-6 md:p-8 grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Left — identity */}
        <div className="md:col-span-2 flex items-start gap-4">
          <div className="w-16 h-16 bevel-btn-primary text-white flex items-center justify-center font-display font-black text-xl flex-shrink-0">
            {initials || "—"}
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-mono text-[10px] tracking-widest uppercase text-muted mb-1">
              {eventTypeLabel(ev.event_type)}{client ? ` · ${client.name}` : ""}
            </div>
            <h2 className="font-display text-2xl md:text-3xl font-black text-app tracking-tight leading-tight truncate">
              {ev.title}
            </h2>
            {ev.couple_names && <p className="text-sm text-muted mt-1">{ev.couple_names}</p>}
            <div className="flex items-center gap-4 mt-4 text-xs text-app flex-wrap">
              <span className="flex items-center gap-1.5"><CalendarDays size={12} strokeWidth={1.75} className="text-muted" />
                {new Date(ev.event_date).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
              </span>
              {ev.venue && <span className="flex items-center gap-1.5"><MapPin size={12} strokeWidth={1.75} className="text-muted" />{ev.venue}</span>}
              {ev.is_recurring && (
                <span className="flex items-center gap-1.5 text-[#F59E0B]"><Repeat size={12} strokeWidth={1.75} />{summarizeRecurrence(ev) || "Recurring"}</span>
              )}
              <ArrowUpRight size={13} strokeWidth={2} className="ml-auto text-muted group-hover:text-app" />
            </div>
          </div>
        </div>
        {/* Right — countdown + readiness */}
        <div className="border-l-0 md:border-l md:pl-6 border-hair flex flex-col items-start gap-4">
          <div>
            <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted">COUNTDOWN</div>
            <div className={`font-mono text-5xl font-black tabular-nums leading-none mt-1 ${isToday ? "text-[#EF4444]" : soon ? "text-[#F59E0B]" : "text-app"}`}>
              {days === 0 ? "TODAY" : days < 0 ? `+${Math.abs(days)}` : days}
            </div>
            {days !== 0 && (
              <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted mt-1">{days < 0 ? "DAYS PAST" : "DAYS OUT"}</div>
            )}
          </div>
          <ReadinessBar readiness={r} />
        </div>
      </div>
    </Link>
  );
}

function ReadinessBar({ readiness }) {
  const score = readiness?.score ?? 0;
  const status = readiness?.status || "critical";
  const c = readiness?.counts || {};
  const color = {
    "critical": "bg-[#EF4444] text-[#EF4444]",
    "in-progress": "bg-[#F59E0B] text-[#F59E0B]",
    "on-track": "bg-[#059669] text-[#059669]",
    "ready": "bg-[#0A0A0A] text-app",
  }[status];
  const [barBg, textColor] = color.split(" ");
  return (
    <div className="w-full" data-testid="readiness-bar">
      <div className="flex items-baseline justify-between mb-1">
        <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted">READINESS</div>
        <div className={`font-mono text-[10px] tracking-[0.2em] uppercase font-bold ${textColor}`}>{status.replace("-", " ")}</div>
      </div>
      <div className="flex items-baseline gap-2">
        <span className="font-mono text-3xl font-black tabular-nums text-app">{score}%</span>
      </div>
      <div className="mt-2 h-1.5 bg-surface-2 w-full">
        <div className={`h-full ${barBg}`} style={{ width: `${score}%` }} />
      </div>
      <div className="mt-2 grid grid-cols-2 gap-x-2 gap-y-0.5 font-mono text-[10px] text-muted">
        {c.tasks_total > 0 && <span>Tasks {c.tasks_done}/{c.tasks_total}</span>}
        {c.vendors_total > 0 && <span>Vendors {c.vendors_confirmed}/{c.vendors_total}</span>}
        {c.guests_total > 0 && <span>Guests {c.guests_responded}/{c.guests_total}</span>}
        {c.budget_planned > 0 && <span>Budget {readiness.budget_pct}%</span>}
      </div>
    </div>
  );
}

function PortfolioWidget({ portfolio, scale, widgetId, editing, collapsed, dragHandleProps, onToggleCollapse, onHide }) {
  const operational = scale === "high-volume";
  if (editing || collapsed) {
    return (
      <Widget
        title="Portfolio stats" sysid="OPS.STATS" testId="widget-portfolio"
        editing={editing} collapsed={collapsed} dragHandleProps={dragHandleProps}
        onToggleCollapse={onToggleCollapse} onHide={onHide}
      >
        <PortfolioStripInner portfolio={portfolio} operational={operational} />
      </Widget>
    );
  }
  return <PortfolioStripInner portfolio={portfolio} operational={operational} />;
}

function PortfolioStripInner({ portfolio, operational }) {
  const cells = operational
    ? [
        { label: "OVERDUE", value: portfolio.overdue_tasks || 0, tone: (portfolio.overdue_tasks || 0) > 0 ? "danger" : "neutral" },
        { label: "AWAITING VENDORS", value: portfolio.vendors_unconfirmed || 0, tone: (portfolio.vendors_unconfirmed || 0) > 0 ? "warn" : "neutral" },
        { label: "DEPOSITS UNPAID", value: portfolio.deposits_unpaid || 0, tone: (portfolio.deposits_unpaid || 0) > 0 ? "warn" : "neutral" },
        { label: "THIS WEEK", value: portfolio.events_this_week || 0 },
        { label: "TODAY", value: portfolio.today_count || 0, tone: (portfolio.today_count || 0) > 0 ? "danger" : "neutral" },
        { label: "TOTAL", value: portfolio.total_events || 0 },
      ]
    : [
        { label: "TOTAL", value: portfolio.total_events || 0 },
        { label: "UPCOMING", value: portfolio.upcoming || 0 },
        { label: "NEXT 30 DAYS", value: portfolio.next_30_days || 0, tone: "positive" },
        { label: "TODAY", value: portfolio.today_count || 0, tone: (portfolio.today_count || 0) > 0 ? "danger" : "neutral" },
      ];
  return (
    <div className={`grid ${operational ? "grid-cols-2 md:grid-cols-3 lg:grid-cols-6" : "grid-cols-2 md:grid-cols-4"} bevel border-hair`}>
      {cells.map((c) => <StatCell key={c.label} {...c} />)}
    </div>
  );
}

function StatCell({ label, value, tone }) {
  const accent = {
    "positive": "text-emerald-400",
    "warn": "text-[#F59E0B]",
    "danger": "text-[#EF4444]",
    "neutral": "text-app",
  }[tone || "neutral"];
  const pulse = tone === "danger" && value > 0;
  return (
    <div className="p-4 border-r border-b border-hair last:border-r-0">
      <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted mb-2 flex items-center gap-1.5">
        {label}
        {pulse && <span className="w-1.5 h-1.5 rounded-full bg-[#EF4444] live-pulse" />}
      </div>
      <div className={`font-mono text-2xl md:text-3xl font-semibold tabular-nums ${accent}`}>{String(value).padStart(2, "0")}</div>
    </div>
  );
}

function FocusWidget({ items, widgetId, editing, collapsed, dragHandleProps, onToggleCollapse, onHide }) {
  return (
    <Widget
      title="Today's focus" sysid="OPS.FOCUS" count={items.length} testId="widget-todays-focus"
      editing={editing} collapsed={collapsed} dragHandleProps={dragHandleProps}
      onToggleCollapse={onToggleCollapse} onHide={onHide}
    >
      {items.length === 0 ? (
        <div className="text-center py-6">
          <CheckCircle2 size={20} strokeWidth={1.5} className="mx-auto text-emerald-400 mb-2" />
          <div className="text-sm text-app">All clear.</div>
          <p className="text-xs text-muted mt-1">Nothing urgent across your events.</p>
        </div>
      ) : (
        <ul className="space-y-1">
          {items.map((f, i) => (
            <li key={`${f.kind}-${f.event_id}-${i}`}>
              <Link
                to={`/events/${f.event_id}`}
                data-testid={`focus-item-${i}`}
                className="flex items-center gap-3 py-2 px-2 -mx-2 hover:bg-inset group"
              >
                <UrgencyDot urgency={f.urgency} />
                <FocusIcon kind={f.kind} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-app truncate">{f.title}</div>
                  <div className="font-mono text-[10px] tracking-wider uppercase text-muted">
                    {f.event_title}
                    {f.due_date && ` · DUE ${new Date(f.due_date).toLocaleDateString(undefined, { month: "short", day: "numeric" }).toUpperCase()}`}
                  </div>
                </div>
                <ArrowUpRight size={13} strokeWidth={2} className="text-faint group-hover:text-app flex-shrink-0" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Widget>
  );
}

function UrgencyDot({ urgency }) {
  const color = { "overdue": "bg-[#EF4444]", "today": "bg-[#F59E0B]", "soon": "bg-app" }[urgency] || "bg-[#A1A1AA]";
  return <span className={`w-1.5 h-1.5 rounded-full ${color} flex-shrink-0 ${urgency === "overdue" ? "live-pulse" : ""}`} />;
}

function FocusIcon({ kind }) {
  const iconMap = {
    "task": <ListTodo size={14} strokeWidth={1.75} className="text-muted" />,
    "vendor": <Users size={14} strokeWidth={1.75} className="text-muted" />,
    "payment": <Wallet size={14} strokeWidth={1.75} className="text-muted" />,
    "event-day": <Sparkles size={14} strokeWidth={1.75} className="text-muted" />,
  };
  return <span className="flex-shrink-0">{iconMap[kind] || <AlertTriangle size={14} strokeWidth={1.75} className="text-muted" />}</span>;
}

function ScheduleWidget({ items, dense, widgetId, editing, collapsed, dragHandleProps, onToggleCollapse, onHide }) {
  return (
    <Widget
      title="Today's schedule" sysid="OPS.AGENDA" count={items.length} testId="widget-upcoming-schedule"
      editing={editing} collapsed={collapsed} dragHandleProps={dragHandleProps}
      onToggleCollapse={onToggleCollapse} onHide={onHide}
    >
      {items.length === 0 ? (
        <div className="text-xs text-muted py-4 text-center">Nothing scheduled today.</div>
      ) : (
        <ul className={dense ? "grid grid-cols-1 md:grid-cols-2 gap-1" : "space-y-1"}>
          {items.map((s) => (
            <li key={`${s.event_id}-${s.time || "no-time"}-${s.title}`}>
              <Link
                to={`/events/${s.event_id}`}
                className="flex items-baseline gap-3 py-1.5 px-2 -mx-2 hover:bg-inset"
              >
                <span className="font-mono text-xs tabular-nums text-app w-12 flex-shrink-0">{s.time || "—"}</span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-app truncate">{s.title}</div>
                  <div className="font-mono text-[10px] tracking-wider uppercase text-muted">
                    {s.event_title}{s.location ? ` · ${s.location}` : ""}
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Widget>
  );
}

function ActivityWidget({ items, widgetId, editing, collapsed, dragHandleProps, onToggleCollapse, onHide }) {
  return (
    <Widget
      title="Recent activity" sysid="OPS.FEED" count={items.length} testId="widget-recent-activity"
      editing={editing} collapsed={collapsed} dragHandleProps={dragHandleProps}
      onToggleCollapse={onToggleCollapse} onHide={onHide}
    >
      {items.length === 0 ? (
        <div className="text-xs text-muted py-4 text-center">No activity yet.</div>
      ) : (
        <ul className="space-y-2">
          {items.map((a) => (
            <li key={`${a.kind}-${a.event_id}-${a.timestamp}-${a.description.slice(0, 24)}`}>
              <Link to={`/events/${a.event_id}`} className="flex items-start gap-2 py-1 hover:bg-inset -mx-1 px-1">
                <ActivityIcon kind={a.kind} />
                <div className="min-w-0 flex-1">
                  <div className="text-xs text-app leading-snug">{a.description}</div>
                  <div className="font-mono text-[10px] tracking-wider uppercase text-faint truncate">
                    {a.event_title} · {formatRelativeTime(a.timestamp)}
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Widget>
  );
}

function ActivityIcon({ kind }) {
  const map = {
    "guest-rsvp": <CheckCircle2 size={12} strokeWidth={1.75} className="text-emerald-400 mt-0.5" />,
    "guest-added": <Users size={12} strokeWidth={1.75} className="text-muted mt-0.5" />,
    "task-done": <CheckCircle2 size={12} strokeWidth={1.75} className="text-emerald-400 mt-0.5" />,
    "task-added": <ListTodo size={12} strokeWidth={1.75} className="text-muted mt-0.5" />,
    "doc-upload": <FileUp size={12} strokeWidth={1.75} className="text-muted mt-0.5" />,
    "update-posted": <MessageSquare size={12} strokeWidth={1.75} className="text-muted mt-0.5" />,
    "member-joined": <Users size={12} strokeWidth={1.75} className="text-emerald-400 mt-0.5" />,
    "member-invited": <Users size={12} strokeWidth={1.75} className="text-muted mt-0.5" />,
  };
  return <span className="flex-shrink-0">{map[kind] || <Activity size={12} strokeWidth={1.75} className="text-muted mt-0.5" />}</span>;
}

function formatRelativeTime(ts) {
  if (!ts) return "—";
  const then = new Date(ts);
  if (Number.isNaN(then.getTime())) return "—";
  const diff = (Date.now() - then.getTime()) / 1000; // seconds
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return then.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function MessagesWidget({ items, widgetId, editing, collapsed, dragHandleProps, onToggleCollapse, onHide }) {
  return (
    <Widget
      title="Messages" sysid="OPS.CHAT" count={items.length} testId="widget-messages"
      editing={editing} collapsed={collapsed} dragHandleProps={dragHandleProps}
      onToggleCollapse={onToggleCollapse} onHide={onHide}
    >
      {items.length === 0 ? (
        <div className="text-xs text-muted py-4 text-center">All caught up.</div>
      ) : (
        <ul className="space-y-2">
          {items.map((m) => (
            <li key={m.message_id}>
              <Link
                to={`/events/${m.event_id}`}
                data-testid={`msg-feed-item-${m.message_id}`}
                className="flex items-start gap-2 py-1.5 hover:bg-inset -mx-1 px-1 group"
              >
                <MessageSquare size={12} strokeWidth={1.75} className="text-muted mt-1 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-xs font-semibold text-app">{m.author_name || "Someone"}</span>
                    {m.is_decision && <span className="font-mono text-[9px] uppercase tracking-wider text-[#059669]">DECISION</span>}
                    {m.pinned && <span className="font-mono text-[9px] uppercase tracking-wider text-[#F59E0B]">PINNED</span>}
                  </div>
                  <div className="text-xs text-app truncate">{m.body || "[attachment]"}</div>
                  <div className="font-mono text-[10px] tracking-wider uppercase text-faint truncate">
                    {m.event_title} · {m.thread_name} · {formatRelativeTime(m.created_at)}
                  </div>
                </div>
                <ArrowUpRight size={12} strokeWidth={2} className="text-faint group-hover:text-app flex-shrink-0 mt-1" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Widget>
  );
}


function EventsWidget({ events, clients, dense, widgetId, editing, collapsed, dragHandleProps, onToggleCollapse, onHide }) {
  const { upcoming, past } = useMemo(() => ({
    upcoming: events.filter((e) => daysUntil(e.event_date) >= 0),
    past: events.filter((e) => daysUntil(e.event_date) < 0),
  }), [events]);
  return (
    <>
      {upcoming.length > 0 && (
        <Widget
          title="Upcoming events" sysid="EVT.UPCOMING" count={upcoming.length} testId="widget-events-upcoming"
          editing={editing} collapsed={collapsed} dragHandleProps={dragHandleProps}
          onToggleCollapse={onToggleCollapse} onHide={onHide}
        >
          <div className={`grid gap-3 ${dense ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1"}`}>
            {upcoming.map((e) => <EventCard key={e.id} ev={e} clients={clients} />)}
          </div>
        </Widget>
      )}
      {past.length > 0 && !collapsed && !editing && (
        <Widget title="Archived" sysid="EVT.ARCHIVED" count={past.length} testId="widget-events-archived">
          <div className="grid gap-3 grid-cols-1 md:grid-cols-2">
            {past.map((e) => <EventCard key={e.id} ev={e} clients={clients} muted />)}
          </div>
        </Widget>
      )}
    </>
  );
}

function EventCard({ ev, muted, clients = [] }) {
  const days = daysUntil(ev.event_date);
  const type = eventTypeLabel(ev.event_type);
  const client = ev.client_id ? clients.find((c) => c.id === ev.client_id) : null;
  const initials = (ev.title || "").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const isToday = days === 0;
  const soon = days >= 0 && days <= 7;
  const r = ev.readiness || {};
  const c = r.counts || {};

  return (
    <Link
      to={`/events/${ev.id}`}
      data-testid={`event-card-${ev.id}`}
      className={`group block bevel border-hair card-lift relative ${muted ? "opacity-60" : ""}`}
    >
      <div className={`absolute left-0 top-0 bottom-0 w-1 ${isToday ? "bg-[#EF4444]" : soon ? "bg-[#F59E0B]" : muted ? "bg-[#E4E4E7]" : "bg-app"}`} />
      <div className="p-4 pl-5">
        <div className="flex items-center justify-between mb-3 font-mono text-[10px] tracking-widest uppercase">
          <span className="text-muted">EVT.{ev.id.slice(-6).toUpperCase()}</span>
          <div className="flex items-center gap-1.5">
            {ev.role && ev.role !== "owner" && (
              <span className="bevel-btn-primary text-white px-1.5 py-0.5">{ev.role}</span>
            )}
            <ArrowUpRight size={13} strokeWidth={2} className="text-muted group-hover:text-app" />
          </div>
        </div>
        <div className="flex items-start gap-3 mb-3">
          <div className="w-11 h-11 bevel-btn-primary text-white flex items-center justify-center font-display font-black text-sm flex-shrink-0">
            {initials || "—"}
          </div>
          <div className="min-w-0 flex-1">
            {client && <div className="font-mono text-[10px] uppercase tracking-widest text-muted truncate">{client.name}</div>}
            <div className="font-display text-base font-bold text-app truncate leading-tight">{ev.title}</div>
            {ev.couple_names && <div className="text-xs text-muted truncate">{ev.couple_names}</div>}
          </div>
        </div>
        <div className="border-t border-hair pt-3 flex items-center justify-between text-xs mb-3">
          <span className="flex items-center gap-1.5 text-app">
            <CalendarDays size={12} strokeWidth={1.75} className="text-muted" />
            {new Date(ev.event_date).toLocaleDateString(undefined, { year: "2-digit", month: "short", day: "2-digit" }).toUpperCase()}
          </span>
          <span className={`font-mono tabular-nums ${isToday ? "text-[#EF4444] font-semibold" : soon ? "text-[#F59E0B] font-semibold" : "text-app"}`}>
            {days === 0 ? "TODAY" : days < 0 ? `+${Math.abs(days)}D` : `${days}D`}
          </span>
        </div>
        {/* Readiness readout */}
        <div className="grid grid-cols-4 gap-1 mb-2">
          <ReadinessCell label="T" value={c.tasks_total ? `${c.tasks_done}/${c.tasks_total}` : "—"} />
          <ReadinessCell label="V" value={c.vendors_total ? `${c.vendors_confirmed}/${c.vendors_total}` : "—"} />
          <ReadinessCell label="G" value={c.guests_total ? `${c.guests_responded}/${c.guests_total}` : "—"} />
          <ReadinessCell label="B" value={r.budget_pct != null ? `${r.budget_pct}%` : "—"} />
        </div>
        <div className="flex items-center gap-2">
          <div className="flex-1 h-1 bg-surface-2">
            <div
              className={`h-full ${
                r.status === "critical" ? "bg-[#EF4444]"
                : r.status === "in-progress" ? "bg-[#F59E0B]"
                : r.status === "on-track" ? "bg-[#059669]"
                : "bg-app"
              }`}
              style={{ width: `${r.score || 0}%` }}
            />
          </div>
          <span className="font-mono text-xs tabular-nums text-app font-semibold w-9 text-right">{r.score || 0}%</span>
        </div>
      </div>
    </Link>
  );
}

function ReadinessCell({ label, value }) {
  return (
    <div className="text-center border border-hair px-1 py-1">
      <div className="font-mono text-[9px] text-faint">{label}</div>
      <div className="font-mono text-[10px] tabular-nums text-app">{value}</div>
    </div>
  );
}

/* ============================================================================
 * NEW EVENT DIALOG (moved into its own subcomponent for clarity)
 * ==========================================================================*/
function NewEventButton({ open, setOpen, form, setForm, clients, create }) {
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="create-event-btn" className="rounded-lg bevel-btn-primary h-10 px-5 font-medium">
          <Plus size={15} strokeWidth={2} className="mr-2" /> New event
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg bevel-lg">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-app">New event</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <Field label="Event title">
            <Input data-testid="event-title-input" placeholder="e.g. Alex & Jordan" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Type">
              <EventTypePicker value={form.event_type} onChange={(v) => setForm({ ...form, event_type: v })} testId="event-type-select" />
            </Field>
            <Field label="Date">
              <Input data-testid="event-date-input" type="date" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} />
            </Field>
          </div>
          <Field label="Venue (optional)">
            <Input data-testid="event-venue-input" placeholder="e.g. The Old Mill, Vermont" value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} />
          </Field>
          <Field label="Host / Client name(s) (optional)">
            <Input placeholder="e.g. Alex & Jordan · ACME Co." value={form.couple_names} onChange={(e) => setForm({ ...form, couple_names: e.target.value })} />
          </Field>
          {clients.length > 0 && (
            <Field label="Attach to a client (optional)">
              <Select value={form.client_id || "none"} onValueChange={(v) => setForm({ ...form, client_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="event-client-select" className="rounded-sm"><SelectValue placeholder="No client" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No client</SelectItem>
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.company ? ` · ${c.company}` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          )}
          <Field label="Description (optional)">
            <Textarea rows={2} className="rounded-sm" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </Field>
          <Field label="Cover image URL (optional)">
            <Input placeholder="https://…" value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} />
          </Field>
          <RecurrenceControl value={{ is_recurring: form.is_recurring, recurrence: form.recurrence }} onChange={(v) => setForm({ ...form, ...v })} />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} data-testid="event-cancel-btn">Cancel</Button>
          <Button data-testid="event-create-submit" onClick={create} className="rounded-lg bevel-btn-primary">Create event</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="eyebrow block mb-2">{label}</Label>
      {children}
    </div>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div className="bevel border-hair p-12 text-center" data-testid="empty-state">
      <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted mb-3">SYSTEM STATE / EMPTY_INDEX</div>
      <div className="font-display text-2xl font-black text-app mb-2 tracking-tight">Ready when you are.</div>
      <p className="text-sm text-muted max-w-md mx-auto mb-6">
        Boot up your first event and this dashboard will start surfacing what needs your attention — from a single wedding to hundreds of concurrent events.
      </p>
      <Button onClick={onCreate} data-testid="empty-create-event-btn" className="rounded-sm bg-[#0A0A0A] hover:bg-black h-10 px-5 font-medium">
        <Plus size={15} strokeWidth={2} className="mr-2" /> Create first event
      </Button>
    </div>
  );
}

// ============ Invitations banner ============
// Rendered at the very top of the dashboard when the current user has one or
// more pending event invitations. Not part of the widget grid on purpose —
// invitations are high-attention items that should never be hidden by user
// layout customization.
function InvitationsBanner({ invitations, onChanged }) {
  const [busyId, setBusyId] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [reviewingId, setReviewingId] = useState(null);

  if (!invitations || invitations.length === 0) return null;

  const respond = async (invite, verb) => {
    setBusyId(`${invite.event_id}-${verb}`);
    try {
      await api.post(`/invite/${invite.event_id}/${verb}`);
      toast.success(verb === "accept" ? `Joined ${invite.event_title}` : "Invitation declined");
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Could not ${verb} invitation`);
    } finally {
      setBusyId(null);
    }
  };

  const visible = expanded ? invitations : invitations.slice(0, 2);
  const reviewing = invitations.find((i) => i.event_id === reviewingId) || null;

  return (
    <>
    <div
      className="mb-6 bevel border-hair overflow-hidden"
      data-testid="dashboard-invitations-banner"
    >
      <div className="px-4 py-3 border-b border-hair bg-inset flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-6 h-6 rounded-full bg-amber-500 text-black flex items-center justify-center shrink-0">
            <Sparkles size={12} strokeWidth={2.5} />
          </span>
          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted">
            AWAITING YOUR RESPONSE
          </div>
          <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-app font-semibold ml-1">
            · {invitations.length} {invitations.length === 1 ? "invite" : "invites"}
          </span>
        </div>
        {invitations.length > 2 && (
          <button
            onClick={() => setExpanded((v) => !v)}
            className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted hover:text-app"
            data-testid="invitations-toggle-expand"
          >
            {expanded ? "Collapse" : `Show all ${invitations.length}`}
          </button>
        )}
      </div>
      <ul className="divide-y divide-[color:var(--hair)]">
        {visible.map((inv) => (
          <li
            key={inv.member_id}
            className="p-4 flex flex-col md:flex-row md:items-center gap-4"
            data-testid={`invitation-card-${inv.event_id}`}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="font-display text-lg font-black text-app tracking-tight truncate">
                  {inv.event_title}
                </span>
                <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted">
                  · {inv.vendor_category || titleCaseRole(inv.role)}
                </span>
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted">
                {inv.event_date && (
                  <span className="inline-flex items-center gap-1">
                    <CalendarDays size={12} /> {formatInviteDate(inv.event_date)}
                  </span>
                )}
                {inv.venue && (
                  <span className="inline-flex items-center gap-1">
                    <MapPin size={12} /> {inv.venue}
                  </span>
                )}
                {inv.invited_by_name && (
                  <span>Invited by <span className="text-app">{inv.invited_by_name}</span></span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => setReviewingId(inv.event_id)}
                className="inline-flex items-center h-9 px-3 border border-hair hover:border-[#0A0A0A] font-mono text-[10px] tracking-[0.2em] uppercase text-app"
                data-testid={`invitation-review-${inv.event_id}`}
              >
                Review invitation <ArrowUpRight size={12} strokeWidth={2.5} className="ml-1.5" />
              </button>
              <button
                onClick={() => respond(inv, "decline")}
                disabled={busyId?.startsWith(inv.event_id)}
                className="inline-flex items-center h-9 px-3 border border-hair hover:border-[#0A0A0A] font-mono text-[10px] tracking-[0.2em] uppercase text-app disabled:opacity-50"
                data-testid={`invitation-decline-${inv.event_id}`}
              >
                <X size={12} strokeWidth={2.5} className="mr-1.5" /> Decline
              </button>
              <button
                onClick={() => setReviewingId(inv.event_id)}
                disabled={busyId?.startsWith(inv.event_id)}
                className="inline-flex items-center h-9 px-3 bg-[#0A0A0A] hover:bg-black text-white font-mono text-[10px] tracking-[0.2em] uppercase disabled:opacity-50"
                data-testid={`invitation-accept-${inv.event_id}`}
              >
                <CheckCircle2 size={12} strokeWidth={2.5} className="mr-1.5" /> Accept
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
    <InvitationReviewDialog
      invitation={reviewing}
      open={!!reviewing}
      onClose={() => setReviewingId(null)}
      onResponded={() => { setReviewingId(null); onChanged?.(); }}
    />
    </>
  );
}

// ============ Invitation Review Dialog ============
// Full-page review modal spawned from the InvitationsBanner. Preserves the
// "see event details + capabilities before committing" UX from the /invite
// page while keeping the invitee inside the dashboard flow.
function InvitationReviewDialog({ invitation, open, onClose, onResponded }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState("idle");

  useEffect(() => {
    if (!open || !invitation) { setPreview(null); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/invite/${invitation.event_id}`);
        if (!cancelled) setPreview(data);
      } catch (e) {
        if (!cancelled) toast.error("Could not load invitation details");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, invitation]);

  const respond = async (verb) => {
    setAction(verb);
    try {
      await api.post(`/invite/${invitation.event_id}/${verb === "accept" ? "accept" : "decline"}`);
      toast.success(verb === "accept" ? `Joined ${invitation.event_title}` : "Invitation declined");
      onResponded?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Could not ${verb} invitation`);
    } finally {
      setAction("idle");
    }
  };

  if (!invitation) return null;
  const inviteRoleLabel = inviteRoleLabelFor(preview?.member) || invitation.vendor_category || titleCaseRole(invitation.role);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-2xl bg-surface border-hair p-0 overflow-hidden" data-testid="invitation-review-dialog">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-hair">
          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted mb-2 flex items-center gap-1.5">
            <Sparkles size={12} strokeWidth={2} /> YOU&apos;RE INVITED
          </div>
          <DialogTitle className="font-display text-3xl font-black text-app tracking-tight leading-none">
            {invitation.event_title}
          </DialogTitle>
          {invitation.invited_by_name && (
            <p className="text-sm text-muted mt-2 leading-relaxed">
              <span className="text-app">{invitation.invited_by_name}</span> invited you to collaborate as{" "}
              <span className="text-app font-medium">{inviteRoleLabel}</span> through AtelisOS.
            </p>
          )}
        </DialogHeader>

        <div className="p-6 max-h-[60vh] overflow-y-auto">
          {loading || !preview ? (
            <div className="py-10 text-center text-muted text-sm">Loading invitation details…</div>
          ) : (
            <InvitePreviewCard preview={preview} />
          )}
        </div>

        <DialogFooter className="px-6 py-4 border-t border-hair bg-inset flex flex-col sm:flex-row gap-2 sm:justify-between">
          <Button
            variant="outline"
            onClick={() => respond("decline")}
            disabled={action !== "idle" || loading}
            className="rounded-sm border-hair h-11"
            data-testid="review-decline-btn"
          >
            <X size={14} className="mr-2" />
            {action === "decline" ? "Declining…" : "Decline invitation"}
          </Button>
          <Button
            onClick={() => respond("accept")}
            disabled={action !== "idle" || loading}
            className="rounded-sm bg-[#0A0A0A] hover:bg-black text-white h-11 px-6"
            data-testid="review-accept-btn"
          >
            <CheckCircle2 size={14} className="mr-2" />
            {action === "accept" ? "Accepting…" : "Accept invitation"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function titleCaseRole(role) {
  if (!role) return "Team member";
  return role.charAt(0).toUpperCase() + role.slice(1);
}

// ============ Role filter hint (small in-page indicator) ============
// The actual role-switcher chip bar lives in the Shell top-nav now (globally
// accessible). We keep a compact hint on the dashboard so users see "you're
// filtered to N of M events" and can clear the filter without hunting for
// the top nav.
function RoleFilterHint({ availableRoles, value, totalCount, filteredCount }) {
  const { setValue } = useRoleFilter();
  if (!availableRoles || availableRoles.length < 2 || value === "all") return null;
  return (
    <div
      className="mb-6 flex items-center gap-3 px-3 py-2 bevel border-hair"
      data-testid="dashboard-role-filter-hint"
    >
      <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted">FILTERED</span>
      <span className="text-sm text-app">
        Showing {filteredCount} of {totalCount} events where you&apos;re a{" "}
        <span className="font-medium">{value === "owner" ? "Planner" : value.charAt(0).toUpperCase() + value.slice(1)}</span>.
      </span>
      <button
        onClick={() => setValue("all")}
        className="ml-auto font-mono text-[10px] tracking-[0.2em] uppercase text-muted hover:text-app"
        data-testid="dashboard-clear-role-filter"
      >
        Clear filter
      </button>
    </div>
  );
}

function formatInviteDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString(undefined, {
      weekday: "short", day: "numeric", month: "short", year: "numeric",
    });
  } catch { return iso; }
}

